import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Stack } from '@mui/material';
import {
  IconUserPlus,
  IconPencilPlus,
  IconBrandCashapp,
  IconFileExport,
  IconCreditCardRefund,
} from '@tabler/icons-react';
import { apiClient } from 'config/api';
import { Alert } from '@atoms';
import { PERMISSIONS, ProductsAvailable } from '@globalConstants';
import {
  useCustomerId,
  useCreateXlsx,
  useEmployees,
  useErrorSnackbar,
  useExportAllUserAccountsFiltered,
  useFastDispersion,
  useFilterInternalFeatures,
  useNamePartnerCustomers,
  usePermissions,
  useProductsByCustomer,
  useRecoveryEmployeeBalance,
  useURLparams,
  useUser,
  useShowAreaFeature,
  useAreasByCustomer,
  useIsAreasAdmin,
  useModal,
} from '@hooks';
import { FullWidthContent, MainMenuLayout } from '@layouts';
import { Table, Toolbar } from '@molecules';
import { DispersionConfirmationModal, FormModal, RecoveryConfirmationModal } from '@organisms';
import { useClientStore } from '@stores';
import { filterDuplicateWalletIds } from '@utils';
import AssignAreaModal from '@modules/employeeDetails/tabs/productsTab/AssignAreaModal';
import AssignNewProduct from './assignNewProduct';
import { getAlertInfo, ALERT_TYPES } from './assignNewProduct/constants';
import {
  COLUMNS,
  DEFAULT_ROWS_PER_PAGE,
  CONFIG_AMOUNT_FIELD,
  VALIDATION_AMOUNT_SCHEMA,
} from './constants';
import useAssignAreaPartial from './data/useAssignAreaPartial';
import usePartnerCustomerMessage from './data/usePartnerCustomerMessage';
import {
  employeeListClick,
  EVENT_EMPLOYEE_LIST_BALANCE_RECOVERY_CANCEL,
  EVENT_EMPLOYEE_LIST_BALANCE_RECOVERY_CONFIRM,
  EVENT_EMPLOYEE_LIST_BALANCE_RECOVERY_GO_TO_BALANCE,
  EVENT_EMPLOYEE_LIST_DISPERSE_BALANCE_CONFIRM,
  EVENT_EMPLOYEE_LIST_DISPERSE_BUTTON,
  EVENT_EMPLOYEE_LIST_DISPERSE_CANCEL,
  EVENT_EMPLOYEE_LIST_DISPERSE_CONFIRM,
  EVENT_EMPLOYEE_LIST_DISPERSE_ERROR,
  EVENT_EMPLOYEE_LIST_PROSPECT_BUTTON,
} from './events';
import SearchBoxClient from './searchBoxEmployees';

const CORPORATE_PRODUCTS = [
  ProductsAvailable.Almuerzo,
  ProductsAvailable.Corporativo,
  ProductsAvailable.Gobierno,
];

/**
 * Check if the product is a corporate one
 * @param {string} product product name
 * @returns {boolean} true if the product is corporate
 */
const isCorporateProduct = (product) => CORPORATE_PRODUCTS.includes(product);

/** Clients screen */
const EmployeesScreen = () => {
  const {
    user: { isInternal },
  } = useUser();
  const { customerId } = useCustomerId();
  const { params, setURLparams } = useURLparams();
  // !isInternal && { page: 1, limit: DEFAULT_ROWS_PER_PAGE, customerId }
  const { ErrorSnackbar, showErrorSnackbar } = useErrorSnackbar();
  const navigate = useNavigate();
  const setClientSelected = useClientStore((state) => state.setClientSelected);
  const setSearchParams = useClientStore((state) => state.setSearchParams);
  const { t } = useTranslation();
  const isDemoDispersion = usePermissions([PERMISSIONS.demoCreateDispersion]);
  const isDemoProspect = usePermissions([PERMISSIONS.demoCreateProspect]);
  const isDemoRecovery = usePermissions([PERMISSIONS.demoUpdateRecoveryBalance]);
  const isDemoAssignProduct = usePermissions([PERMISSIONS.demoUpdateUserProducts]);
  const canViewProspectsButton = usePermissions([PERMISSIONS.prospect]) || isDemoProspect;
  const canViewSendMoneyButton = usePermissions([PERMISSIONS.sendMoney]) || isDemoDispersion;
  const canRecoveryBalanceButton = usePermissions([PERMISSIONS.recoveryBalance]) || isDemoRecovery;
  const canViewAddProductButton =
    usePermissions([PERMISSIONS.assignUserProducts]) || isDemoAssignProduct;
  const { showAreaFeature } = useShowAreaFeature();
  const { data: areas } = useAreasByCustomer({ customerId });
  const filteredColumns = useFilterInternalFeatures(COLUMNS(t, showAreaFeature));
  const tableColumns = useMemo(() => {
    return filteredColumns.filter((item) => !item?.hide);
  }, [filteredColumns]);
  const [differentCustomersAlertInfo, setDifferentCustomersAlertInfo] = useState(null);
  const [isShowAmountModal, setIsShowAmountModal] = useState(false);
  const [modal, setModal] = useState({ showModal: false });
  const [isSelectedAll, setIsSelectedAll] = useState(false);
  const [message, setMessage] = useState(null);
  const {
    createRecoveryEmployeeBalance,
    createRecoveryEmployeeBalanceTotal,
    isLoading: isLoadingRecoveryEmployeeBalance,
    confirmRecoveryEmployeeBalance,
  } = useRecoveryEmployeeBalance();
  const { data: productsData, isLoading: isProductsLoading } = useProductsByCustomer({
    customerId,
    type: 'BUSINESS',
    allowRequest: Boolean(customerId),
  });
  const { data: customerMessages } = usePartnerCustomerMessage();
  const { isAreasAdmin } = useIsAreasAdmin();

  const [confirmDispersion, setConfirmDispersion] = useState({
    openModal: false,
    id: null,
    total: null,
    count: null,
  });

  const [confirmRecovery, setConfirmRecovery] = useState({
    openModal: false,
    recoveryId: null,
    totalAmount: null,
    count: null,
    failedAccounts: [],
    failedCount: null,
  });

  const {
    data,
    isLoading,
    isValidating,
    mutate,
    handlePageChange: onPageChange,
    handleRowsPerPageChange,
    filtersOpen,
    setFiltersOpen,
    onFiltersFormSubmit,
    assignProductOpen,
    setAssignProductOpen,
    selectedItems,
    setSelectedItems,
  } = useEmployees({
    params,
    setURLparams,
    /** Handle the errors of the request */
    onError: function handleErrors(error) {
      switch (error?.response?.status) {
        case 400:
          showErrorSnackbar(t('EMPLOYEES:ERRORS:INVALID_SEARCH'));
          break;
        default:
          showErrorSnackbar(t('EMPLOYEES:ERRORS:GENERIC_ERROR'));
      }
    },
  });
  const {
    closeModal: closeAssignAreaModal,
    data: customerAreas,
    open: isAssignAreaModalOpen,
    openModal: openAssignAreaModal,
  } = useModal();
  const { isLoading: isAssignAreaLoading, postAssignAreaPartial } = useAssignAreaPartial(
    setModal,
    closeAssignAreaModal,
    mutate
  );

  const { getNamePartnerCustomer } = useNamePartnerCustomers();
  const companyName = getNamePartnerCustomer(customerId);

  const isCorporate = productsData?.length > 0;

  const {
    isLoading: isLoadingDispersion,
    fastDispersion,
    confirmFastDispersion,
    isError,
    setIsError,
    errorMessage,
    createFastDispersionWithAllFilteredData,
  } = useFastDispersion();

  const { isLoading: isLoadingCreateXlsx, createXlsx } = useCreateXlsx();

  const { isLoading: isLoadingExportAll, exportAllUserAccountsFiltered } =
    useExportAllUserAccountsFiltered();

  useEffect(
    function updateCompanyIdParamWhenChangeSelectedCompany() {
      if (!params.customerId) {
        return;
      }
      if (!isInternal && customerId !== parseInt(params.customerId)) {
        setURLparams({ customerId });
      }
    },
    [params, customerId, setURLparams, isInternal]
  );

  /**
   * Store client data & searched params & go to movement detail screen
   */
  function navigateToDetail(client) {
    setClientSelected(client);
    setSearchParams(params);
    navigate(`/detalle-cliente/${client.username}`, {
      state: { params, account_number: client.acctNumber },
    });
  }
  /**
   * Run the action if all the areas are empty
   * @param {function} action function to run
   */
  const handleEmptyAreasCheck = (action) => {
    const accountWithArea = selectedItems.some((user) => user.areaId !== null);
    if (accountWithArea) {
      const alertInfo = getAlertInfo(
        { error_different_areas: true, alertType: ALERT_TYPES.ALREADY_HAVE_AREAS },
        t
      );
      setDifferentCustomersAlertInfo(alertInfo);
    } else {
      action();
    }
  };

  /**
   * Run the action if none of the selected corporate accounts have balance
   * @param {function} action function to run
   */
  const handleZeroBalanceCheck = (action) => {
    const accountWithBalance = selectedItems.some(
      (user) => Number(user.balance) && isCorporateProduct(user.products)
    );
    if (accountWithBalance) {
      setDifferentCustomersAlertInfo(getAlertInfo({ alertType: ALERT_TYPES.HAS_BALANCE }, t));
    } else {
      action();
    }
  };

  /**
   * Handles the logic for checking if all selected users belong to the same customer.
   * If they do, it performs a specified action. Otherwise, it shows an alert.
   * @param {Function} action - The action to perform if all customers are the same.
   */
  const handleCustomerCheck = (action) => {
    const hasNullCustomerId = selectedItems.some((user) => user.customerId === null);
    const alertInfo = getAlertInfo(
      { error_different_customers: true, alertType: ALERT_TYPES.DIFFERENT_CUSTOMERS },
      t
    );
    if (!hasNullCustomerId) {
      const allCustomerIdsEqual = selectedItems.every((user, index, array) => {
        if (index < array.length - 1) {
          return user.customerId === array[index + 1].customerId;
        }
        return true;
      });

      const allAreaIdsEqual = selectedItems.every((user, index, array) => {
        if (index < array.length - 1) {
          return user.areaId === array[index + 1].areaId;
        }
        return true;
      });

      const hasMultipleAreas =
        ((isAreasAdmin || isInternal) && areas.length > 0) || (!isAreasAdmin && areas.length > 1);
      const isEmptySelectedArea = isSelectedAll && hasMultipleAreas && !params.areaId;

      const customerIdFromParamsOrStore = isInternal ? params?.customerId : customerId;
      if (
        (isSelectedAll && customerIdFromParamsOrStore && !isEmptySelectedArea) ||
        (!isSelectedAll && allCustomerIdsEqual && allAreaIdsEqual)
      ) {
        action();
      } else if (isSelectedAll && !customerIdFromParamsOrStore) {
        setDifferentCustomersAlertInfo(alertInfo);
      } else if (!allAreaIdsEqual || isEmptySelectedArea) {
        setDifferentCustomersAlertInfo(
          getAlertInfo({ error_different_areas: true, alertType: ALERT_TYPES.DIFFERENT_AREAS }, t)
        );
      } else {
        setDifferentCustomersAlertInfo(alertInfo);
      }
    } else {
      setDifferentCustomersAlertInfo(alertInfo);
    }
  };

  /** Handles the assignment of a new product to selected users. */
  const handleAssignNewProduct = () => handleCustomerCheck(() => setAssignProductOpen(true));

  /** Handles the dispersion of selected users.*/
  const handleDispersion = () => handleCustomerCheck(() => setIsShowAmountModal(true));

  /** Handles exporting dispersion data.*/
  const handleExportAccounts = () => {
    if (isSelectedAll) {
      exportAllUserAccountsFiltered({
        customerId,
        lastName: params.lastName,
        firstName: params.firstName,
        email: params.email,
        cuit: params.cuit,
        walletId: params.walletId,
        product: params.product,
        categories: params.categories,
        areaId: params.areaId,
      });
    } else {
      createXlsx(selectedItems);
    }
  };

  /**
   * Handles the retrieval of balance for selected users.
   * Checks if the selected users have the required products and if the total balance is positive.
   * If both conditions are met, it proceeds with the retrieval process. Otherwise, it displays an alert.
   */
  const handlerRetrieveBalance = async () => {
    const selectedItemsAreCorporate = selectedItems.every(({ products }) =>
      isCorporateProduct(products)
    );
    if (!selectedItemsAreCorporate) {
      setDifferentCustomersAlertInfo({
        description: t('EMPLOYEES:RETRIEVE_BALANCE:PRODUCTS_NOT_ALLOWED_DESCRIPTION'),
        title: t('EMPLOYEES:RETRIEVE_BALANCE:PRODUCTS_NOT_ALLOWED_TITLE'),
        modalVisible: true,
      });
      return;
    }

    const totalBalance = selectedItems.reduce((sum, { balance }) => {
      return !isNaN(Number(balance)) ? sum + Number(balance) : sum;
    }, 0);
    if (!totalBalance > 0) {
      setDifferentCustomersAlertInfo({
        description: t('EMPLOYEES:RETRIEVE_BALANCE:POSITIVE_BALANCE_DESCRIPTION'),
        title: t('EMPLOYEES:RETRIEVE_BALANCE:POSITIVE_BALANCE_TITLE'),
        modalVisible: true,
      });
      return;
    }

    if (isDemoRecovery) {
      setConfirmRecovery({
        openModal: true,
        recoveryId: null,
        totalAmount: totalBalance,
        count: selectedItems.length,
        failedAccounts: [],
        failedCount: 0,
      });
      return;
    }

    isSelectedAll ? retrieveBalanceMassive() : retrieveBalanceSelected();
  };

  /** Handles confirmation of a balance recovery for selected accounts */
  const retrieveBalanceMassive = () => {
    createRecoveryEmployeeBalanceTotal(params)
      .then((recovery) => {
        if (recovery?.recoveryId) {
          setConfirmRecovery({ openModal: true, ...recovery });
        }
      })
      .catch((e) => {
        if (e.response && e.response.status === 406) {
          setDifferentCustomersAlertInfo({
            description: t('EMPLOYEES:RETRIEVE_BALANCE:PRODUCTS_NOT_ALLOWED_DESCRIPTION'),
            title: t('EMPLOYEES:RETRIEVE_BALANCE:PRODUCTS_NOT_ALLOWED_TITLE'),
            modalVisible: true,
          });
          return;
        }
        setModal({
          showModal: true,
          title: 'EMPLOYEES:RETRIEVE_BALANCE:RECOVERY_ERROR_TITLE',
          description: 'EMPLOYEES:RETRIEVE_BALANCE:RECOVER_ERROR_BODY',
          variant: 'error',
        });
      });
  };

  /** Handles confirmation of a balance recovery for all selected accounts */
  const retrieveBalanceSelected = () => {
    const accountsInfo = selectedItems.map(({ balance, acctNumber, cuit, accountId }) => ({
      balance,
      accountNumber: Number(acctNumber),
      cuil: cuit,
      id: accountId,
    }));
    createRecoveryEmployeeBalance(customerId, accountsInfo)
      .then((recovery) => {
        if (recovery?.recoveryId) {
          setConfirmRecovery({ openModal: true, ...recovery });
        }
      })
      .catch((e) => {
        if (e.response && e.response.status === 406) {
          setDifferentCustomersAlertInfo({
            description: t('EMPLOYEES:RETRIEVE_BALANCE:PRODUCTS_NOT_ALLOWED_DESCRIPTION'),
            title: t('EMPLOYEES:RETRIEVE_BALANCE:PRODUCTS_NOT_ALLOWED_TITLE'),
            modalVisible: true,
          });
          return;
        }
        setModal({
          showModal: true,
          title: 'EMPLOYEES:RETRIEVE_BALANCE:RECOVERY_ERROR_TITLE',
          description: 'EMPLOYEES:RETRIEVE_BALANCE:RECOVER_ERROR_BODY',
          variant: 'error',
        });
      });
  };

  const selectedHasGiftCard = selectedItems.some(
    ({ products }) => products === ProductsAvailable.GiftCard
  );

  const buttons = [
    {
      text: t(isInternal ? 'EMPLOYEES:PROSPECT_CREATE' : 'MENU:PROSPECTS:ACCOUNT'),
      onClick: () => {
        employeeListClick(EVENT_EMPLOYEE_LIST_PROSPECT_BUTTON);
        navigate(isInternal ? '/prealta/rapida' : '/alta-de-cuenta');
      },
      icon: <IconUserPlus />,
      show: canViewProspectsButton,
    },
    {
      text: t('DISPERSIONS:DISPERSE'),
      onClick: () => {
        employeeListClick(EVENT_EMPLOYEE_LIST_DISPERSE_BUTTON);
        handleDispersion();
      },
      icon: <IconBrandCashapp />,
      disabled: !selectedItems.length > 0,
      show: canViewSendMoneyButton && !selectedHasGiftCard,
      isLoading: isLoadingDispersion,
    },
    {
      text: t('EMPLOYEES:ASSIGN_PRODUCT:BUTTON'),
      onClick: handleAssignNewProduct,
      icon: <IconPencilPlus />,
      disabled: !selectedItems.length > 0 || isSelectedAll,
      show: canViewAddProductButton && !selectedHasGiftCard,
    },
    {
      text: t('EMPLOYEES:DETAIL:ASSIGN_AREA'),
      onClick: () =>
        handleEmptyAreasCheck(() =>
          handleCustomerCheck(() => handleZeroBalanceCheck(() => openAssignAreaModal(areas)))
        ),
      icon: <IconPencilPlus />,
      disabled: !selectedItems.length > 0 || isSelectedAll,
      show: isInternal && !selectedHasGiftCard,
    },
    {
      text: t('COMMON:EXPORT'),
      onClick: handleExportAccounts,
      icon: <IconFileExport />,
      disabled: !selectedItems.length > 0,
      isLoading: isLoadingExportAll || isLoadingCreateXlsx,
      show: false,
    },
    {
      text: t('EMPLOYEES:DETAIL:RETRIEVE_BALANCE'),
      onClick: handlerRetrieveBalance,
      icon: <IconCreditCardRefund />,
      disabled: !selectedItems.length > 0,
      isLoading: isProductsLoading || isLoadingRecoveryEmployeeBalance,
      show: canRecoveryBalanceButton && customerId && isCorporate,
    },
  ];
  const filteredButtons = buttons.filter((button) => button.show);
  /** Check if the selected or current time is available to do a dispersion
   *
   * @returns false if the time is unavailable
   */
  const checkAvailableTime = async () => {
    const {
      data: { serverTime },
    } = await apiClient.get('/internal/current-time');
    const currentTime = new Date(serverTime);

    const hours = currentTime.getHours();
    const minutes = currentTime.getMinutes();

    if (hours === 0 && minutes < 30) {
      setModal({
        showModal: true,
        title: 'DISPERSIONS:ERRORS:UNAVAILABLE_TIME_TITLE',
        description: 'DISPERSIONS:ERRORS:UNAVAILABLE_TIME_DESCRIPTION',
        variant: 'error',
      });
      return false;
    }
    return true;
  };
  /** Handle submit fast dispersion. */
  const handleSubmit = async ({ amount, message }) => {
    employeeListClick(EVENT_EMPLOYEE_LIST_DISPERSE_BALANCE_CONFIRM);
    let fastDispersionResponse;
    setMessage(message);
    setIsShowAmountModal(false);
    if (!(await checkAvailableTime())) return;

    if (isSelectedAll) {
      fastDispersionResponse = await createFastDispersionWithAllFilteredData({
        customerId,
        amount,
        filters: {
          cuil: params.cuil,
          firstName: params.firstName,
          lastName: params.lastName,
          categories: params.categories,
          product: params.product,
          ...(areas?.length > 0 && { areaId: selectedItems[0]?.areaId ?? 'none' }),
        },
        message,
      });
    } else {
      fastDispersionResponse = await fastDispersion({
        employees: selectedItems,
        amount,
        customerId,
        message,
        areaId: selectedItems[0]?.areaId ?? 'none',
      });
    }

    const { dispersionId, total, count } = fastDispersionResponse;

    if (dispersionId && total && count) {
      setConfirmDispersion({
        openModal: true,
        id: dispersionId,
        total,
        count,
      });
    }
  };

  /** Handle submit confirmation fast dispersion. */
  const handleDispersionConfirm = async (confirm) => {
    employeeListClick(EVENT_EMPLOYEE_LIST_DISPERSE_CONFIRM);
    const data = await confirmFastDispersion({
      dispersionId: confirmDispersion.id,
      confirm,
    });
    if (data) {
      if (confirm) {
        setSelectedItems([]);
        setModal({
          showModal: true,
          title: 'DISPERSIONS:FAST_DISPERSION:DISPERSIONS_SUCCESS_TITLE_INSTANT',
          description: 'DISPERSIONS:FAST_DISPERSION:DISPERSIONS_SUCCESS_BODY_INSTANT',
          variant: 'info',
          confirmButtonText: 'DISPERSIONS:GO_TO_DISPERSIONS',
          handleSubmit: () => {
            navigate('/dispersiones/mis-dispersiones');
          },
          customStyles: { description: { whiteSpace: 'pre-line' } },
          closeButtonText: 'COMMON:CLOSE',
        });
      }
    }
    setConfirmDispersion({ openModal: false, id: null, total: null, count: null });
  };

  /** Closes recovery modal without calling the API */
  const handleRecoveryClose = () => {
    setConfirmRecovery({
      openModal: false,
      recoveryId: null,
      totalAmount: null,
      count: null,
      failedAccounts: [],
      failedCount: null,
    });
  };

  /** Handle submit confirmation recovery balance */
  const handleRecoveryConfirm = (confirm) => {
    if (isDemoRecovery || !confirmRecovery.recoveryId) {
      handleRecoveryClose();
      return;
    }
    employeeListClick(
      confirm
        ? EVENT_EMPLOYEE_LIST_BALANCE_RECOVERY_CONFIRM
        : EVENT_EMPLOYEE_LIST_BALANCE_RECOVERY_CANCEL
    );
    confirmRecoveryEmployeeBalance(confirmRecovery.recoveryId, confirm)
      .then(({ status }) => {
        if (status && confirm) {
          setSelectedItems([]);
          setModal({
            showModal: true,
            title: 'EMPLOYEES:RETRIEVE_BALANCE:RECOVERY_SUCCESS_TITLE',
            description: `EMPLOYEES:RETRIEVE_BALANCE:RECOVER_SUCCESS_BODY${
              isInternal ? '_INTERNAL' : ''
            }`,
            variant: 'success',
            confirmButtonText: `EMPLOYEES:RETRIEVE_BALANCE:RECOVERY_GO_TO_MY_BALANCE${
              isInternal ? '_INTERNAL' : ''
            }`,
            handleSubmit: () => {
              navigate(isInternal ? `/clientes?customerId=${customerId}` : '/saldo');
              employeeListClick(EVENT_EMPLOYEE_LIST_BALANCE_RECOVERY_GO_TO_BALANCE);
            },
          });
          mutate();
        }
      })
      .catch(() => {
        setModal({
          showModal: true,
          title: 'EMPLOYEES:RETRIEVE_BALANCE:RECOVERY_ERROR_TITLE',
          description: 'EMPLOYEES:RETRIEVE_BALANCE:RECOVER_ERROR_BODY',
          variant: 'error',
        });
      })
      .finally(() => {
        handleRecoveryClose();
      });
  };

  /** Handle action when page change */
  const handlePageChange = (page) => {
    onPageChange(page);
    if (isSelectedAll) {
      setIsSelectedAll(false);
      setSelectedItems([]);
    }
  };

  /** Handles select all data */
  const handleSelectAll = () => {
    setIsSelectedAll((prev) => {
      if (prev) {
        setSelectedItems([]);
      }

      return !prev;
    });
  };
  return (
    <MainMenuLayout>
      <FullWidthContent>
        <Stack spacing={3}>
          <SearchBoxClient
            isOpen={filtersOpen}
            onClose={() => setFiltersOpen(false)}
            params={params && params}
            isLoading={isLoading || isValidating}
            retry={mutate}
            onFiltersFormSubmit={onFiltersFormSubmit}
            disabledExportAll={!data?.page.length}
          />
          {filteredButtons.length > 0 && <Toolbar buttons={filteredButtons} />}
          <Table
            count={data?.totalCount || 0}
            items={data?.page}
            isLoading={isLoading}
            columns={tableColumns}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            onItemClick={navigateToDetail}
            page={params.page || 1}
            rowsPerPage={params.limit || DEFAULT_ROWS_PER_PAGE}
            selectedItems={selectedItems}
            setSelectedItems={setSelectedItems}
            checkedItemProp='acctNumber'
            onSelectAll={handleSelectAll}
            isSelectedAll={isSelectedAll}
            setIsSelectedAll={setIsSelectedAll}
            showCheckbox
          />
        </Stack>
        <AssignNewProduct
          assignProductOpen={assignProductOpen}
          setAssignProductOpen={setAssignProductOpen}
          selectedUsers={filterDuplicateWalletIds(selectedItems)}
          disableConfirm={isDemoAssignProduct}
        />
        <ErrorSnackbar />
        <Alert
          modalVisible={differentCustomersAlertInfo?.modalVisible}
          handleClose={() => setDifferentCustomersAlertInfo(null)}
          variant={differentCustomersAlertInfo?.variant}
          title={differentCustomersAlertInfo?.title}
          description={differentCustomersAlertInfo?.description}
          closeButtonText={t('COMMON:ACCEPT')}
        />
        <FormModal
          title='DISPERSIONS:FAST_DISPERSION:TITLE_AMOUNT'
          description='DISPERSIONS:FAST_DISPERSION:DESCRIPTION_AMOUNT'
          isOpen={isShowAmountModal}
          validationSchema={VALIDATION_AMOUNT_SCHEMA}
          configFields={CONFIG_AMOUNT_FIELD(customerMessages?.length)}
          onClose={() => {
            employeeListClick(EVENT_EMPLOYEE_LIST_DISPERSE_CANCEL);
            setIsShowAmountModal(false);
          }}
          initialValues={{ amount: '' }}
          showClearFilter={false}
          onSubmit={handleSubmit}
          disableConfirm={isDemoDispersion}
        />
        <DispersionConfirmationModal
          openModal={confirmDispersion.openModal}
          handleConfirmation={handleDispersionConfirm}
          total={confirmDispersion.total}
          count={confirmDispersion.count}
          companyName={companyName}
          submitLoading={isLoadingDispersion}
          message={message}
          disabled={isDemoDispersion}
        />
        <RecoveryConfirmationModal
          isOpen={confirmRecovery.openModal}
          handleConfirmation={handleRecoveryConfirm}
          onClose={handleRecoveryClose}
          totalAmount={confirmRecovery.totalAmount}
          count={confirmRecovery.count}
          companyName={companyName}
          failedCount={confirmRecovery.failedCount}
          submitLoading={isLoadingRecoveryEmployeeBalance}
          disabled={isDemoRecovery}
        />
        <AssignAreaModal
          areas={customerAreas ?? []}
          postAssignArea={(selectedArea) => {
            postAssignAreaPartial({
              areaName: selectedArea,
              areaId: customerAreas?.find((item) => item.name === selectedArea)?.id,
              accountIds: filterDuplicateWalletIds(selectedItems),
            });
          }}
          isOpen={isAssignAreaModalOpen}
          onClose={closeAssignAreaModal}
          isLoading={isAssignAreaLoading}
          description='EMPLOYEES:ASSIGN_AREA_MODAL:MODAL_DESCRIPTION_PARTIAL'
        />
        <Alert
          modalVisible={isError}
          handleClose={() => {
            employeeListClick(EVENT_EMPLOYEE_LIST_DISPERSE_ERROR);
            setIsError(false);
          }}
          variant='error'
          title={errorMessage.title}
          description={errorMessage.description}
        />
        <Alert
          modalVisible={modal.showModal}
          handleClose={() => setModal({ ...modal, showModal: false })}
          variant={modal.variant}
          title={modal.title}
          description={modal.description}
          {...(modal.confirmButtonText && { confirmButtonText: modal.confirmButtonText })}
          {...(modal.handleSubmit && { handleSubmit: modal.handleSubmit })}
          {...(modal.customStyles && { customStyles: modal.customStyles })}
          {...(modal.closeButtonText && { closeButtonText: modal.closeButtonText })}
        />
      </FullWidthContent>
    </MainMenuLayout>
  );
};

export default EmployeesScreen;
